import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { CreateAccountModel, UserAddressModel } from "src/app/models/create-account.model";
import { Utils } from "src/app/utils/utils";
import { AppValidator } from "src/app/utils/validator";
import { COUNTRY_TIMEZONE_DATA, CountryTimezone } from "./country-timezone-data";
import { AppSessionStorageService } from "src/app/services/utils/session-storage.service";

export class CreateAccountHelper {

    showPassword = false;
    showConfirmPassword = false;
    capsLockOn: boolean = false;
    filteredTimezones: CountryTimezone[] = [];
    countries: CountryTimezone[] = COUNTRY_TIMEZONE_DATA;
    inputModel: CreateAccountModel = new CreateAccountModel();
    createAccountForm = CreateAccountHelper.buildCreateAccountForm(this.countries);

    /**
     * Returns the dial code (international calling code) for a given country code.
     *
     * @param countryCode - The ISO 3166-1 alpha-2 country code (e.g., 'US', 'IN', 'GB').
     * @returns The corresponding dial code as a string (e.g., '+1', '+91'), or an empty string if not found.
     */
    static getDialCode(countryCode: string): string {
        // Map of ISO country codes to their respective dial codes
        const dialCodeMap: Record<string, string> = {
            US: '+1',
            IN: '+91',
            GB: '+44',
            // Add other country mappings here as needed
        };
        // Return the dial code if it exists in the map, otherwise return an empty string
        return dialCodeMap[countryCode] || '';
    }

    /**
     * Converts a string to Title Case with added spacing between camelCase or alphanumeric words.
     * 
     * Examples:
     * - "helloWorld" ➝ "Hello World"
     * - "userID123" ➝ "User ID 123"
     *
     * @param str - The input string to convert.
     * @returns A title-cased string with proper spacing.
     */
    static toTitleCase(str: string): string {
        return str
            // Add a space between lowercase letters followed by uppercase letters or numbers (e.g., "userID" → "user ID")
            .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
            // Add space before standalone numbers (e.g., "item12" → "item 12")
            .replace(/([0-9]+)/g, ' $1')
            // Add space between letters followed by numbers (e.g., "version2" → "version 2")
            .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
            // Capitalize the first letter of each word
            .replace(/\b\w/g, (char) => char.toUpperCase())
            // Trim leading/trailing whitespace
            .trim();
    }

    /**
     * Capitalizes the first letter of the input text.
     *
     * @param text - The input string.
     * @returns The string with the first character capitalized.
     */
    static capitalize(text: string): string {
        // Capitalize the first character and append the rest of the string
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    /**
     * Generates a user-friendly error message based on validation errors of a form control.
     *
     * @param control - The form control to check for validation errors.
     * @param fieldName - The name of the field (used in the error message).
     * @returns A string with the appropriate error message or null if there are no errors.
     */
    static getErrorMessage(control: AbstractControl | null, fieldName: string): string | null {
        // Check if the control exists, has been touched, and contains errors
        if (control && control.touched && control.errors) {
            // Required field validation
            if (control.errors['required']) {
                return `${this.toTitleCase(fieldName)} is required.`;
                // Minimum length validation
            } else if (control.errors['minlength']) {
                return `Minimum ${control.errors['minlength'].requiredLength} characters required.`;
                // Maximum length validation
            } else if (control.errors['maxlength']) {
                return `Maximum ${control.errors['maxlength'].requiredLength} characters allowed.`;
                // Pattern mismatch (e.g., regex) validation
            } else if (control.errors['pattern']) {
                return `${this.toTitleCase(fieldName)} has invalid format.`;
                // Custom password validation error
            } else if (control.errors['customPasswordError']) {
                return control.errors['customPasswordError'];
                // Invalid phone format based on selected country
            } else if (control.errors['invalidPhone']) {
                return `Invalid phone number for the selected country`;
            }
        }
        // No errors or untouched control
        return null;
    }

    /**
     * Aggregates and returns a combined error message string based on a list of form control fields.
     *
     * It checks for the following validations:
     * - Required fields
     * - Minimum length fields
     * - Invalid phone format (based on custom validation)
     *
     * @param formGroup - The form group containing form controls to validate.
     * @param fields - An array of control field names to be checked.
     * @returns A single string combining all the relevant error messages.
     */
    static getCombinedErrors(formGroup: FormGroup, fields: string[]): string {
        // Human-readable labels for form field names
        const fieldLabels: { [key: string]: string } = {
            phoneNumber: 'phone number',
            countryCode: 'country code',
            username: 'username',
            firstname: 'first name',
            lastname: 'last name',
            streetAddress1: 'street address 1',
            streetAddress2: 'street address 2',
            city: 'city',
            stateProvince: 'state/province',
            zipCode: 'zip code',
            customerNumber: 'customer number',
            orgName: 'organization name',
            timezone: 'timezone',
            password: 'password',
            confirmPassword: 'confirm password',
        };

        const missingFields: string[] = [];
        const minLengthFields: string[] = [];
        const invalidPhoneFields: string[] = [];

        // Iterate through each specified field and collect validation errors
        fields.forEach((field) => {
            const control = formGroup.get(field);
            if (control && control.touched && control.errors) {
                if (control.errors['required']) {
                    missingFields.push(fieldLabels[field] || field);
                }
                if (control.errors['minlength']) {
                    const minLen = control.errors['minlength'].requiredLength;
                    minLengthFields.push(`${fieldLabels[field] || field} (min ${minLen} chars)`);
                }
                if (control.errors['invalidPhone']) {
                    invalidPhoneFields.push(fieldLabels[field] || field);
                }
            }
        });

        const errors: string[] = [];

        // Construct error messages for missing fields
        if (missingFields.length === 1) {
            errors.push(`${this.capitalize(missingFields[0])} is required.`);
        } else if (missingFields.length === 2) {
            errors.push(`Both ${missingFields[0]} and ${missingFields[1]} are required.`);
        } else if (missingFields.length > 2) {
            const last = missingFields.pop();
            errors.push(`All of ${missingFields.join(', ')}, and ${last} are required.`);
        }

        // Construct error messages for minlength fields
        if (minLengthFields.length === 1) {
            errors.push(`${this.capitalize(minLengthFields[0])} is too short.`);
        } else if (minLengthFields.length >= 2) {
            const last = minLengthFields.pop();
            errors.push(`All of ${minLengthFields.join(', ')}, and ${last} are too short.`);
        }

        // Construct error messages for invalid phone number fields
        if (invalidPhoneFields.length === 1) {
            errors.push(`${this.capitalize(invalidPhoneFields[0])} is invalid for selected country.`);
        } else if (invalidPhoneFields.length >= 2) {
            const last = invalidPhoneFields.pop();
            errors.push(`All of ${invalidPhoneFields.join(', ')}, and ${last} are invalid for selected country.`);
        }

        // Join all messages into one string
        return errors.join(' ');
    }



    /**
     * Builds and returns a reactive form group for creating a new user account.
     * 
     * This form group includes fields for user profile, address, organization, phone details,
     * and password validation (including confirm password check and custom password strength check).
     *
     * @param countries - An array of country objects used to prefill the default timezone.
     * @returns A FormGroup with validation rules for each field.
     */
    static buildCreateAccountForm(countries: any[]): FormGroup {
        return new FormGroup(
            {
                // Username: required, 3–30 characters
                username: new FormControl('', [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(30)
                ]),
                // First name: required, 2–30 characters
                firstname: new FormControl('', [
                    Validators.required,
                    Validators.minLength(2),
                    Validators.maxLength(30)
                ]),
                // Last name: required, 2–30 characters
                lastname: new FormControl('', [
                    Validators.required,
                    Validators.minLength(2),
                    Validators.maxLength(30)
                ]),
                // Street address line 1: required, max 100 characters
                streetAddress1: new FormControl('', [
                    Validators.required,
                    Validators.maxLength(100)
                ]),
                // Street address line 2: required, max 100 characters
                streetAddress2: new FormControl('', [
                    Validators.required,
                    Validators.maxLength(100)
                ]),
                // State or province: required, max 50 characters
                stateProvince: new FormControl('', [
                    Validators.required,
                    Validators.maxLength(50)
                ]),
                // City: required, max 50 characters
                city: new FormControl('', [
                    Validators.required,
                    Validators.maxLength(50)
                ]),
                // Zip code: required, 3–10 characters (alphanumeric and hyphen allowed)
                zipCode: new FormControl('', [
                    Validators.required,
                    Validators.maxLength(10),
                    Validators.pattern('^[a-zA-Z0-9- ]{3,10}$')
                ]),
                // Customer number: optional, max 50 characters
                customerNumber: new FormControl('', [
                    Validators.maxLength(50)
                ]),
                // Organization name: optional, max 50 characters
                orgName: new FormControl('', [
                    Validators.maxLength(50)
                ]),
                // Country code: required
                countryCode: new FormControl('', Validators.required),
                // Phone number: required, must match country-specific and general numeric rules
                phoneNumber: new FormControl('', [
                    Validators.required,
                    Validators.minLength(5),
                    Validators.pattern('^[0-9]{7,15}$'),
                    this.validatePhoneNumberByCountry()
                ]),
                // Timezone: required; defaults to the first country name from the list
                timezone: new FormControl(countries[0]?.countryName || '', Validators.required),
                // Password: required, custom validator applied
                password: new FormControl('', [
                    Validators.required,
                    (control: AbstractControl) => {
                        const error = AppValidator.validatePassword(control.value || '');
                        return error ? { customPasswordError: error } : null;
                    }
                ]),
                // Confirm password: required
                confirmPassword: new FormControl('', Validators.required)
            },
            {
                // Apply cross-field validator to ensure password and confirmPassword match
                validators: this.isValidPassword
            }
        );
    }

    /**
     * Validator to check if the password and confirmPassword fields in a form group match.
     *
     * This is a cross-field validator applied at the form group level.
     *
     * @param group - The AbstractControl (typically a FormGroup) containing password fields.
     * @returns A validation error object `{ passwordMismatch: true }` if passwords don't match, otherwise `null`.
     */
    static isValidPassword(group: AbstractControl): { [key: string]: any } | null {
        const password = group.get('password')?.value;
        const confirmPassword = group.get('confirmPassword')?.value;
        // Check if password and confirmPassword are equal
        return password === confirmPassword ? null : { passwordMismatch: true };
    }

    /**
     * Validator factory to validate phone number format based on selected country code.
     *
     * This custom validator dynamically accesses the countryCode from the form group
     * and validates the phone number using a utility method.
     *
     * @returns A ValidatorFn that returns `{ invalidPhone: true }` if invalid, otherwise `null`.
     */
    static validatePhoneNumberByCountry(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const group = control.parent;
            if (!group) return null;

            const countryCode = group.get('countryCode')?.value;
            const phone = control.value;

            // Skip validation if required values are missing
            if (!phone || !countryCode) return null;

            // Perform country-specific phone number validation
            if (!Utils.isValidPhoneNumber(countryCode, phone)) {
                return { invalidPhone: true };
            }

            return null;
        };
    }

    /**
     * Maps the values from a reactive form into a CreateAccountModel instance.
     * 
     * This method takes values from the form and assigns them to the corresponding properties 
     * in the model, including creating and populating the nested UserAddressModel.
     *
     * @param form - The FormGroup instance containing user input for account creation.
     * @returns The populated CreateAccountModel.
     */
    mapFormToModel(form: FormGroup): CreateAccountModel {
        const formValue = form.value;

        // Assign top-level properties from the form to the model
        const model = this.inputModel;
        model.username = formValue.username;
        model.firstname = formValue.firstname;
        model.lastname = formValue.lastname;
        model.timezone = formValue.timezone;
        model.phone_number = formValue.phoneNumber;
        model.country_code = formValue.countryCode;
        model.zip_code = formValue.zipCode || '';
        model.org_name = formValue.orgName || '';
        model.customer_number = formValue.customerNumber || '';
        model.password = formValue.password || '';

        // Map nested address fields into the UserAddressModel
        model.address = new UserAddressModel();
        model.address.street_address1 = formValue.streetAddress1;
        model.address.street_address2 = formValue.streetAddress2;
        model.address.state = formValue.stateProvince;
        model.address.city = formValue.city;
        model.address.zip_code = formValue.zipCode || '';

        return model;
    }


    /**
     * Retrieves the validation error message for a specific form field.
     * 
     * @param fieldName - The name of the form field to check.
     * @returns A human-readable error message if validation fails, otherwise null.
     */
    getErrorMessage(fieldName: string): string | null {
        return CreateAccountHelper.getErrorMessage(this.createAccountForm.get(fieldName), fieldName);
    }

    /**
     * Aggregates error messages for a list of fields in the form.
     * 
     * Useful for displaying a summary of multiple validation issues at once.
     * 
     * @param fields - An array of field names to check for validation errors.
     * @returns A combined error message string for all invalid fields.
     */
    getCombinedErrors(fields: string[]): string {
        return CreateAccountHelper.getCombinedErrors(this.createAccountForm, fields);
    }

    /**
     * Detects if Caps Lock is enabled during a keyboard event.
     * 
     * This is commonly used for password fields to alert users if Caps Lock might affect their input.
     * 
     * @param event - The keyboard event to inspect.
     */
    checkCapsLock(event: KeyboardEvent): void {
        this.capsLockOn = event.getModifierState && event.getModifierState('CapsLock');
    }


    /**
     * Toggles the visibility of the password or confirm password input field.
     * 
     * This is typically used to show or hide the password text when the user clicks a toggle icon.
     * 
     * @param field - The field to toggle, either 'password' or 'confirmPassword'.
     */
    togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
        if (field === 'password') {
            // Toggle the boolean controlling visibility of the password field
            this.showPassword = !this.showPassword;
        } else if (field === 'confirmPassword') {
            // Toggle the boolean controlling visibility of the confirm password field
            this.showConfirmPassword = !this.showConfirmPassword;
        }
    }
    /**
     * Filters the list of countries based on a keyword match.
     *
     * Performs a case-insensitive search on both the country name and country code.
     *
     * @param keyword - The keyword to search for in country name or country code.
     * @returns An array of matching CountryTimezone objects.
     */
    searchCountries(keyword: string): CountryTimezone[] {
        const lowerKeyword = keyword.toLowerCase();
        // Filter countries where either name or code includes the search keyword
        return this.countries.filter(
            (country) =>
                country.countryName.toLowerCase().includes(lowerKeyword) ||
                country.countryCode.toLowerCase().includes(lowerKeyword)
        );
    }

    /**
     * Handles the input change event for the search field.
     *
     * Updates the `filteredTimezones` list based on the user's input.
     *
     * @param event - The input event triggered by user interaction.
     */
    onSearchInputChanged(event: Event): void {
        const target = event.target as HTMLInputElement;

        // Filter the countries based on the user's input
        this.filteredTimezones = this.searchCountries(target.value);
    }

    /**
     * Handles the event when a country is selected from the dropdown.
     *
     * Updates the form's `countryCode` and `timezone` fields based on the selected country.
     *
     * @param selected - The selected country object containing country code and name.
     */
    onSelectCountry(selected: CountryTimezone): void {
        this.createAccountForm.patchValue({
            // Set the dial code for the selected country code
            countryCode: CreateAccountHelper.getDialCode(selected.countryCode),
            // Set the timezone field using the selected country's name
            timezone: selected.countryName,
        });
    }


    /**
     * Clears focus from the currently active element after clearing the timezone input.
     *
     * This is used to close the dropdown or autocomplete panel once the timezone input is cleared,
     * improving the user experience.
     */
    onTimezoneClear(): void {
        setTimeout(() => {
            // If there's an active element, remove focus from it
            if (document.activeElement) {
                (document.activeElement as HTMLElement).blur();
            }
        }, 0);
    }

    /**
     * Custom search function used for filtering countries in a dropdown or autocomplete input.
     *
     * @param term - The search term entered by the user.
     * @param item - The item from the country list to match against (should have `countryName` and `countryCode`).
     * @returns `true` if the item matches the search term, otherwise `false`.
     */
    customSearchFn(term: string, item: any): boolean {
        term = term.toLowerCase();
        // Match against lowercase country name or country code
        return (
            item.countryName.toLowerCase().includes(term) ||
            item.countryCode.toLowerCase().includes(term)
        );
    }

    /**
     * Handles the selection of a timezone from the dropdown/autocomplete.
     * Updates the form's `countryCode` field based on the selected item.
     *
     * @param selected - The selected item from the timezone list (should include a `countryCode`).
     */
    onTimezoneSelected(selected: any): void {
        if (selected && selected.countryCode) {
            // Update the form with the selected country code
            this.createAccountForm.patchValue({ countryCode: selected.countryCode });
        }
    }

    /**
     * Initializes form fields with default country code and timezone.
     * Uses the first item in the countries list.
     */
    public setupDefaultCountryAndTimezone(): void {
        const defaultCountry = this.countries[0];
        if (!defaultCountry) return;
        const dialCode = CreateAccountHelper.getDialCode(defaultCountry.countryCode);
        const defaultTimezone = defaultCountry.countryName?.[0] ?? '';
        // Patch default values
        this.createAccountForm.patchValue({
            countryCode: dialCode,
            timezone: defaultTimezone
        });
        // Set filtered timezone list and reassign form fields
        this.filteredTimezones = this.countries;
        const firstTimezone = this.filteredTimezones[0];
        if (firstTimezone) {
            this.createAccountForm.patchValue({
                timezone: firstTimezone.countryName,
                countryCode: firstTimezone.countryCode
            });
        }
    }
}
